import React from 'react';
import { 
  Camera, 
  Wallet, 
  Globe, 
  CheckCircle2, 
  ArrowUpRight,
  TrendingUp,
  ShieldCheck,
  Zap,
    Lock,
    FileText,
    UserCheck
} from 'lucide-react';

export default function WhoItsFor() {
  const personas = [
    {
      title: "For Creators",
      subtitle: "Producers, Storytellers, & Crew",
      description: "Access global capital without geographical barriers. Tokenize your 'skill equity' and ensure you are paid fairly for your expertise and time spent on set.",
      features: ["Verified Skill-Equity tracking", "Instant production budget access", "Retain creative control"],
      buttonText: "Register as Creator",
      icon: <Camera className="w-8 h-8" />,
      theme: "dark"
    },
    {
      title: "For Investors",
      subtitle: "Financial Backers & Strategic Contributors",
      description: "Invest in the future of African cinema. Gain direct ownership of film IPs and enjoy transparent, automated ROI through our smart contract revenue share.",
      features: ["On-chain proof of ownership", "Automated dividends & exits", "Diversified cinematic assets"],
      buttonText: "Start Investing",
      icon: <Wallet className="w-8 h-8" />,
      theme: "accent"
    },
    {
      title: "For The Diaspora",
      subtitle: "Global African Community",
      description: "Directly support and profit from the authentic portrayal of African stories on the global stage. Bridge the gap between home and the world.",
      features: ["Direct cultural impact", "Early access to premieres", "Exclusive community rewards"],
      buttonText: "Join the Diaspora",
      icon: <Globe className="w-8 h-8" />,
      theme: "light"
    }
  ];

  return (
    <div className="bg-white min-h-screen font-sans text-slate-900">
      
      {/* --- HEADER SECTION --- */}
      <section className="pt-20 pb-16 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight">
            Who Is It <span className="text-[#c07d3e]">For?</span>
          </h1>
          <p className="text-slate-500 text-lg md:text-xl leading-relaxed">
            MbogiCapital bridges the gap between creative vision and financial backing, 
            powered by transparent blockchain technology.
          </p>
        </div>
      </section>

      {/* --- PERSONA CARDS --- */}
      <section className="py-12 px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {personas.map((persona, idx) => (
            <div 
              key={idx} 
              className={`p-10 rounded-[3rem] flex flex-col h-full transition-transform hover:-translate-y-2 duration-300 shadow-sm ${
                persona.theme === 'dark' ? 'bg-slate-900 text-white' : 
                persona.theme === 'accent' ? 'bg-[#c07d3e] text-white' : 
                'bg-slate-50 border border-slate-100 text-slate-900'
              }`}
            >
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-8 ${
                persona.theme === 'light' ? 'bg-white shadow-sm text-[#c07d3e]' : 'bg-white/10 text-white'
              }`}>
                {persona.icon}
              </div>
              
              <h2 className="text-3xl font-bold mb-2">{persona.title}</h2>
              <p className={`text-sm font-semibold uppercase tracking-widest mb-6 ${
                persona.theme === 'light' ? 'text-[#c07d3e]' : 'text-orange-200/80'
              }`}>
                {persona.subtitle}
              </p>
              
              <p className={`mb-10 leading-relaxed flex-grow ${
                persona.theme === 'light' ? 'text-slate-500' : 'text-slate-200/80'
              }`}>
                {persona.description}
              </p>

              <ul className="space-y-4 mb-10">
                {persona.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm font-medium">
                    <CheckCircle2 className={`w-5 h-5 ${
                      persona.theme === 'light' ? 'text-green-500' : 'text-orange-300'
                    }`} />
                    {feature}
                  </li>
                ))}
              </ul>

              <button className={`w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all ${
                persona.theme === 'dark' ? 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700' :
                persona.theme === 'accent' ? 'bg-white hover:bg-orange-50 text-[#c07d3e]' :
                'bg-[#0a1d2e] hover:bg-slate-800 text-white'
              }`}>
                {persona.buttonText} <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* --- INNOVATION HIGHLIGHT --- */}
     <section className="py-24 px-6 bg-slate-50 border-y border-slate-100 mt-12">
  <div className="max-w-7xl mx-auto">
    <div className="flex flex-col lg:flex-row items-center gap-16">
      
      {/* Left Side: Copy */}
      <div className="lg:w-1/2">
        <span className="text-[#c07d3e] font-bold text-sm tracking-widest uppercase">The Mbogi Guarantee</span>
        <h2 className="text-3xl md:text-5xl font-extrabold mt-4 mb-8 leading-tight text-[#0a1d2e]">
          The Rules are Written in Stone.
        </h2>
        <p className="text-slate-500 text-lg mb-10 leading-relaxed">
          We use technology to ensure that nobody—not even the platform—can change the deal once a film project starts. Your equity and your earnings are protected by automated rules.
        </p>
        
        <div className="space-y-8">
          <div className="flex gap-6">
            <div className="flex-shrink-0 w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center text-[#c07d3e] border border-orange-50">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h4 className="font-bold text-xl mb-2 text-[#0a1d2e]">Unbreakable Ownership</h4>
              <p className="text-slate-500">Once you earn your stake, it is yours. It cannot be reduced by gatekeepers or middle-men.</p>
            </div>
          </div>

          <div className="flex gap-6">
            <div className="flex-shrink-0 w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center text-[#c07d3e] border border-orange-50">
              <Zap className="w-6 h-6" />
            </div>
            <div>
              <h4 className="font-bold text-xl mb-2 text-[#0a1d2e]">Automatic Payouts</h4>
              <p className="text-slate-500">When the film makes a profit, the system sends your share directly to you. No waiting for accounting months.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side: The Visual "Governance Certificate" */}
      <div className="lg:w-1/2 w-full">
        <div className="bg-white rounded-[3rem] p-8 md:p-12 relative overflow-hidden shadow-xl border border-slate-200">
          
          {/* Header */}
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-50 rounded-full flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#c07d3e]" />
              </div>
              <p className="font-black text-[#0a1d2e] tracking-tight uppercase text-sm">Smart Agreement</p>
            </div>
            <div className="flex items-center gap-2 bg-green-50 px-3 py-1 rounded-full border border-green-100">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-[10px] font-bold text-green-700 uppercase">Enforced</span>
            </div>
          </div>

          {/* Hard Rules List */}
          <div className="space-y-4">
            {[
              { 
                label: "Safety Escrow", 
                text: "Funds are locked and released only when work is verified.",
                icon: <Lock className="w-4 h-4" />
              },
              { 
                label: "Global Transparency", 
                text: "Every cent in and out of the project is visible to all contributors.",
                icon: <Globe className="w-4 h-4" />
              },
              { 
                label: "Zero Middle-Men", 
                text: "Payments go from the audience directly to the creators.",
                icon: <UserCheck className="w-4 h-4" />
              }
            ].map((rule, i) => (
              <div key={i} className="flex items-center gap-5 p-5 bg-slate-50 rounded-2xl border border-slate-100 group transition-all hover:bg-white hover:shadow-md">
                <div className="text-slate-400 group-hover:text-[#c07d3e] transition-colors">
                  {rule.icon}
                </div>
                <div>
                  <p className="font-bold text-sm text-[#0a1d2e]">{rule.label}</p>
                  <p className="text-xs text-slate-500 leading-tight mt-1">{rule.text}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Footer Security Note */}
          <div className="mt-10 pt-8 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="mt-10 pt-8 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex -space-x-3">
                    {[
                    "https://randomuser.me/api/portraits/men/32.jpg",
                    "https://randomuser.me/api/portraits/women/44.jpg",
                    "https://randomuser.me/api/portraits/men/46.jpg",
                    "https://randomuser.me/api/portraits/women/68.jpg"
                    ].map((url, i) => (
                    <img 
                        key={i} 
                        src={url} 
                        alt="Mbogi Member" 
                        className="w-10 h-10 rounded-full border-2 border-white object-cover shadow-sm"
                    />
                    ))}
                    <div className="w-10 h-10 rounded-full border-2 border-white bg-[#0a1d2e] flex items-center justify-center text-[10px] text-white font-bold shadow-sm">
                    +1k
                    </div>
                </div>
                <p className="text-[10px] text-slate-400 font-medium italic text-center sm:text-right max-w-[150px]">
                    Verified by 1,200+ Mbogi Community Members
                </p>
            </div>
            
          </div>

          {/* Decorative Corner */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-orange-50 -mr-16 -mt-16 rounded-full opacity-50" />
        </div>
      </div>
      
    </div>
  </div>
</section>
      {/* --- FINAL CTA --- */}
      <section className="py-24 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-extrabold mb-8">Ready to script the next success story?</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-10 py-4 bg-[#c07d3e] text-white rounded-2xl font-bold shadow-lg shadow-orange-100 hover:scale-105 transition-transform">
              Join the Ecosystem
            </button>
            <button className="px-10 py-4 bg-slate-100 text-slate-600 rounded-2xl font-bold hover:bg-slate-200 transition-colors">
              View Current Projects
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}